import type { ReactNode } from "react";
import { Avatar, AvatarStack } from "@/components/ui/Avatar";
import { Button, IconButton } from "@/components/ui/Button";
import { FlowMark, LogicLineMark } from "@/components/ui/Logo";
import { StatusPill, type PillStyle } from "@/components/ui/StatusPill";
import { BRAND, SCALE, STATUS_PALETTE, tagColor, type StatusColor } from "@/lib/tokens";
import { sampleUsers } from "@/lib/sample-data";

const TAG_DEMO = [
  "B2B",
  "DACH",
  "Video",
  "Case Study",
  "Events",
  "E-Mail",
  "Newsletter",
  "Paid",
  "LinkedIn",
  "Lead-Gen",
  "Website",
  "Partner",
  "Vertrieb",
  "Content",
  "Produkt",
  "Webinar",
];

export function SystemPage() {
  const statusEntries = Object.entries(STATUS_PALETTE) as [
    StatusColor,
    (typeof STATUS_PALETTE)[StatusColor],
  ][];

  return (
    <div style={{ flex: 1, overflow: "auto", padding: "28px 36px 80px", background: "var(--bg)" }}>
      <div style={{ maxWidth: 1180, margin: "0 auto" }}>
        <div style={{ marginBottom: 22 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: SCALE[500],
              textTransform: "uppercase",
              letterSpacing: "0.08em",
            }}
          >
            Flow Design System
          </div>
          <h1
            style={{
              margin: "6px 0 4px",
              fontSize: 26,
              fontWeight: 600,
              letterSpacing: "-0.015em",
              color: "var(--ink-1)",
            }}
          >
            Bausteine
          </h1>
          <p
            style={{ margin: 0, fontSize: 13.5, color: SCALE[500], maxWidth: 640, lineHeight: 1.5 }}
          >
            Diese Seite ist die Referenz für Entwickler. Alle Werte sind aus dem LogicLine&nbsp;CVI
            (Stand 17.04.2024) abgeleitet.
          </p>
        </div>

        <Section title="Brand-Farben" subtitle="Direkt aus dem CVI">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
            {[
              { name: "LogicLine Blau", hex: BRAND.blue, role: "Primär" },
              { name: "LogicLine Orange", hex: BRAND.orange, role: "Akzent / CTA" },
              { name: "Blau dunkel", hex: BRAND.blueDark, role: "Body-Text" },
              { name: "Blau-Grau hell", hex: BRAND.blueGreyLt, role: "Flächen" },
            ].map((s) => (
              <Swatch key={s.name} {...s} />
            ))}
          </div>
        </Section>

        <Section title="Skala" subtitle="50 → 900, interpoliert aus den CVI-Tokens">
          <div
            style={{
              display: "flex",
              borderRadius: 10,
              overflow: "hidden",
              border: "1px solid var(--hairline)",
            }}
          >
            {(Object.entries(SCALE) as [string, string][]).map(([k, v]) => {
              const onDark = Number.parseInt(k, 10) >= 500;
              return (
                <div
                  key={k}
                  style={{
                    flex: 1,
                    height: 64,
                    background: v,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-end",
                    padding: 8,
                    color: onDark ? "#fff" : BRAND.blueDark,
                    fontSize: 10,
                    fontFamily: "JetBrains Mono Variable, JetBrains Mono, ui-monospace, monospace",
                  }}
                >
                  <div style={{ fontWeight: 600 }}>{k}</div>
                  <div style={{ opacity: 0.7 }}>{v}</div>
                </div>
              );
            })}
          </div>
        </Section>

        <Section title="Status-Palette" subtitle="10 ruhige Tints — Soft Fill + dunkler Text">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(5, 1fr)",
              gap: 10,
              marginBottom: 14,
            }}
          >
            {statusEntries.map(([k, v]) => (
              <div
                key={k}
                style={{
                  background: "var(--surface)",
                  borderRadius: 10,
                  padding: 12,
                  border: "1px solid var(--hairline)",
                }}
              >
                <StatusPill option={{ color: k, label: v.label }} size="sm" />
                <div style={{ display: "flex", gap: 6, marginTop: 12 }}>
                  <Mini c={v.bg} />
                  <Mini c={v.fg} />
                  <Mini c={v.dot} />
                </div>
                <div
                  style={{
                    fontSize: 10.5,
                    color: SCALE[500],
                    marginTop: 8,
                    fontFamily: "JetBrains Mono Variable, JetBrains Mono, ui-monospace, monospace",
                  }}
                >
                  status.{k}
                </div>
              </div>
            ))}
          </div>

          <div
            style={{
              background: "var(--surface)",
              borderRadius: 10,
              padding: 16,
              border: "1px solid var(--hairline)",
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: SCALE[500],
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                marginBottom: 12,
              }}
            >
              Drei Varianten
            </div>
            <div style={{ display: "flex", gap: 22, alignItems: "center", flexWrap: "wrap" }}>
              {(
                [
                  { id: "tint" as PillStyle, label: "Tint (default)" },
                  { id: "outline" as PillStyle, label: "Outline" },
                  { id: "dot" as PillStyle, label: "Dot" },
                ] as const
              ).map((s) => (
                <div key={s.id}>
                  <div
                    style={{ fontSize: 11, color: SCALE[500], marginBottom: 6, fontWeight: 500 }}
                  >
                    {s.label}
                  </div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {statusEntries.slice(0, 5).map(([k, v]) => (
                      <StatusPill key={k} option={{ color: k, label: v.label }} pillStyle={s.id} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Section>

        <Section title="Buttons">
          <div
            style={{
              background: "var(--surface)",
              borderRadius: 10,
              padding: 18,
              border: "1px solid var(--hairline)",
            }}
          >
            <Subsection label="Varianten">
              <Button variant="primary" icon="plus">
                Primär
              </Button>
              <Button variant="accent" icon="plus">
                Akzent
              </Button>
              <Button variant="secondary" icon="upload">
                Secondary
              </Button>
              <Button variant="ghost" icon="filter">
                Ghost
              </Button>
              <Button variant="destructive" icon="trash">
                Destructive
              </Button>
            </Subsection>
            <Subsection label="Größen">
              <Button variant="primary" size="sm">
                Small
              </Button>
              <Button variant="primary" size="md">
                Medium
              </Button>
              <Button variant="primary" size="lg">
                Large
              </Button>
            </Subsection>
            <Subsection label="Icon-only" last>
              <IconButton icon="search" />
              <IconButton icon="filter" />
              <IconButton icon="dotsV" />
              <IconButton icon="bell" />
              <IconButton icon="settings" />
            </Subsection>
          </div>
        </Section>

        <Section title="Avatare">
          <div
            style={{
              background: "var(--surface)",
              borderRadius: 10,
              padding: 18,
              border: "1px solid var(--hairline)",
            }}
          >
            <Subsection label="Größen">
              <Avatar user={sampleUsers[0]} size={16} />
              <Avatar user={sampleUsers[0]} size={20} />
              <Avatar user={sampleUsers[0]} size={24} />
              <Avatar user={sampleUsers[0]} size={32} />
              <Avatar user={sampleUsers[0]} size={40} />
            </Subsection>
            <Subsection label="Stack mit Overflow">
              <AvatarStack users={sampleUsers} size={26} max={4} />
              <AvatarStack users={sampleUsers.slice(0, 3)} size={26} max={3} />
              <AvatarStack users={sampleUsers.slice(0, 1)} size={26} max={3} />
            </Subsection>
            <Subsection label="Mit Presence" last>
              <Avatar user={sampleUsers[0]} size={32} presence />
              <Avatar user={sampleUsers[1]} size={32} presence />
              <Avatar user={sampleUsers[2]} size={32} />
            </Subsection>
          </div>
        </Section>

        <Section title="Logo" subtitle="LogicLine-CVI · plus Flow-Quadrat-Mark">
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 12 }}>
            <div
              style={{
                background: BRAND.blue,
                borderRadius: 10,
                padding: "28px 24px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 110,
              }}
            >
              <LogicLineMark height={32} />
            </div>
            <div
              style={{
                background: "var(--surface)",
                borderRadius: 10,
                padding: 18,
                border: "1px solid var(--hairline)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 12,
              }}
            >
              <FlowMark size={56} />
              <div
                style={{
                  fontSize: 11,
                  color: SCALE[500],
                  fontFamily: "JetBrains Mono Variable, JetBrains Mono, ui-monospace, monospace",
                }}
              >
                Flow / 56px
              </div>
            </div>
          </div>
        </Section>

        <Section title="Tag-Farben" subtitle="Deterministisch aus Tag-Name gehasht — 8 Tints">
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {TAG_DEMO.map((tag) => (
              <span
                key={tag}
                style={{
                  fontSize: 12,
                  padding: "4px 10px",
                  borderRadius: 5,
                  fontWeight: 500,
                  background: tagColor(tag),
                  color: SCALE[700],
                }}
              >
                {tag}
              </span>
            ))}
          </div>
        </Section>
      </div>
    </div>
  );
}

function Section({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
}) {
  return (
    <div style={{ marginBottom: 32 }}>
      <div style={{ marginBottom: 12 }}>
        <h2
          style={{
            margin: 0,
            fontSize: 15,
            fontWeight: 600,
            color: "var(--ink-1)",
            letterSpacing: "-0.005em",
          }}
        >
          {title}
        </h2>
        {subtitle ? (
          <p style={{ margin: "2px 0 0", fontSize: 12.5, color: "var(--ink-3)" }}>{subtitle}</p>
        ) : null}
      </div>
      {children}
    </div>
  );
}

function Subsection({
  label,
  last,
  children,
}: {
  label: string;
  last?: boolean;
  children: ReactNode;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        flexWrap: "wrap",
        padding: "14px 0",
        borderBottom: last ? "none" : "1px solid var(--hairline-soft)",
      }}
    >
      <span
        style={{
          width: 110,
          fontSize: 11,
          color: "var(--ink-3)",
          fontFamily: "JetBrains Mono Variable, JetBrains Mono, ui-monospace, monospace",
        }}
      >
        {label}
      </span>
      {children}
    </div>
  );
}

function Swatch({ name, hex, role }: { name: string; hex: string; role: string }) {
  return (
    <div
      style={{
        borderRadius: 10,
        border: "1px solid var(--hairline)",
        overflow: "hidden",
        background: "var(--surface)",
      }}
    >
      <div style={{ height: 70, background: hex }}></div>
      <div style={{ padding: 12 }}>
        <div style={{ fontSize: 12.5, fontWeight: 500, color: "var(--ink-1)" }}>{name}</div>
        <div style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 1 }}>{role}</div>
        <div
          style={{
            fontSize: 10.5,
            marginTop: 6,
            color: "var(--ink-3)",
            fontFamily: "JetBrains Mono Variable, JetBrains Mono, ui-monospace, monospace",
          }}
        >
          {hex}
        </div>
      </div>
    </div>
  );
}

function Mini({ c }: { c: string }) {
  return (
    <div
      style={{
        width: 18,
        height: 18,
        borderRadius: 5,
        background: c,
        boxShadow: "inset 0 0 0 1px rgba(13,62,99,0.06)",
      }}
    ></div>
  );
}
